// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getWarehouse } from "@bg/App/Services/PostalCodeService";
import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    makeHandlerChainAwaitable,
} from "@insite/client-framework/HandlerCreator";
import { Session, updateSessionWithResult } from "@insite/client-framework/Services/SessionService";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import { getLocationPathAndQuery } from "@insite/client-framework/Store/Data/Pages/PageSelectors";

export interface PostalCodeParameter {
    guestPostalCode: string;
    hasUserChangedPostal?: boolean;
}

interface Result {
    name?: string;
    id?: string;
    successful: boolean;
    message?: string;
    session?: Session;
}

type HandlerType = ApiHandler<PostalCodeParameter & HasOnSuccess & HasOnError, Result>;

export const PopulateApiParameters: HandlerType = props => {
    props.apiParameter = props.parameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    props.apiResult = await getWarehouse(props.apiParameter);
};

export const UpdateSession: HandlerType = async props => {
    if (props.apiResult && props.apiResult.successful) {
        const session = props.getState()?.context.session;
        const newSession = {
            pickupWarehouse: {
                ...session.pickUpWarehouse,
                id: props.apiResult.id,
            },
        } as unknown as Session;

        if (props.parameter.hasUserChangedPostal || window.localStorage.getItem("signedOut") === "true") {
            newSession.fulfillmentMethod = "PickUp";

            if (window.localStorage.getItem("signedOut") === "true") {
                window.localStorage.removeItem("signedOut");
            }
        }

        const result = await updateSessionWithResult({
            session: newSession,
        });
        if (result.successful) {
            window.localStorage.setItem("postalCodeID", props.apiResult.id!);
            window.localStorage.setItem("postalCode", props.parameter.guestPostalCode);
            props.apiResult.session = result.result;
        } else {
            props.parameter.onError?.(result.errorMessage as any);
            return false;
        }
    }
};

// OOTB code to update the site context
export const ChangeCustomerContext: HandlerType = async props => {
    if (props.apiResult && props.apiResult.successful) {
        const awaitableChangeCustomerContext = makeHandlerChainAwaitable(changeCustomerContext as any);

        await awaitableChangeCustomerContext({
            billToId: props.apiResult.session?.billToId,
            shipToId: props.apiResult.session?.shipToId,
            fulfillmentMethod: props.apiResult.session?.fulfillmentMethod,
            pickUpWarehouse: props.apiResult.session?.pickUpWarehouse,
            returnUrl: getLocationPathAndQuery(props.getState()),
            onError: (errorMessage: string) => {
                props.parameter.onError?.(errorMessage as any);
            },
        })(props.dispatch, props.getState);
    }
};

export const onSuccess: HandlerType = props => {
    if (props.apiResult && props.apiResult.successful) {
        props.parameter.onSuccess?.(props.apiResult.name as any);
    }
};

export const OnError: HandlerType = props => {
    if (props.apiResult && !props.apiResult.successful) {
        props.parameter.onError?.(props.apiResult.message as any);
    }
};

export const chain = [
    PopulateApiParameters,
    RequestDataFromApi,
    UpdateSession,
    ChangeCustomerContext,
    onSuccess,
    OnError,
];

const getWarehouseData = createHandlerChainRunner(chain, "GetWarehouse");
export default getWarehouseData;
